import React, { useState } from 'react'
import {
    FiX,
    FiCopy,
    FiMail,
    FiLink,
    FiShare2,
} from 'react-icons/fi'
import { FaTwitter, FaLinkedin, FaFacebook, FaWhatsapp } from 'react-icons/fa'
import { useSnackbarContext } from './SnackbarProvider'

interface ShareModalProps {
    isOpen: boolean
    onClose: () => void
    storyTitle: string
    storyUrl: string
}

const ShareModal: React.FC<ShareModalProps> = ({
    isOpen,
    onClose,
    storyTitle,
    storyUrl,
}) => {
    const { showSuccess, showError } = useSnackbarContext()
    const [copied, setCopied] = useState(false)

    if (!isOpen) return null

    const shareMessage = `Check out my success story on SideGO: "${storyTitle}"`
    const encodedUrl = encodeURIComponent(storyUrl)
    const encodedMessage = encodeURIComponent(shareMessage)

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(storyUrl)
            setCopied(true)
            showSuccess('Link copied to clipboard!', { position: 'bottom-right' })
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            showError('Failed to copy link', { position: 'bottom-right' })
        }
    }

    const shareOptions = [
        {
            id: 'twitter',
            name: 'Twitter',
            icon: FaTwitter,
            color: 'hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900',
            url: `https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedUrl}`,
        },
        {
            id: 'linkedin',
            name: 'LinkedIn',
            icon: FaLinkedin,
            color: 'hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900',
            url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        },
        {
            id: 'facebook',
            name: 'Facebook',
            icon: FaFacebook,
            color: 'hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900',
            url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        },
        {
            id: 'whatsapp',
            name: 'WhatsApp',
            icon: FaWhatsapp,
            color: 'hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900',
            url: `https://wa.me/?text=${encodedMessage}%20${encodedUrl}`,
        },
        {
            id: 'email',
            name: 'Email',
            icon: FiMail,
            color: 'hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700',
            url: `mailto:?subject=${encodeURIComponent(storyTitle)}&body=${encodedMessage}%20${encodedUrl}`,
        },
    ]

    const handleSocialShare = (url: string, platform: string) => {
        window.open(url, '_blank', 'width=600,height=400')
        showSuccess(`Sharing to ${platform}...`, { position: 'bottom-right' })
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 sm:p-0">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-md w-full max-h-96 overflow-y-auto scrollbar-hide">
                {/* Header */}
                <div className="sticky top-0 flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
                            <FiShare2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" />
                        </div>
                        <h2 className="text-lg sm:text-lg font-bold text-gray-900 dark:text-white">
                            Share Your Story
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        aria-label="Close modal"
                    >
                        <FiX className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6">
                    {/* Story Title */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 line-clamp-2">
                        <span className="font-semibold">Story:</span> {storyTitle}
                    </p>

                    {/* Copy Link Section */}
                    <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">
                            Copy Link
                        </h3>
                        <button
                            onClick={handleCopyLink}
                            className="w-full flex items-center gap-3 px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-400 transition-all group"
                        >
                            <FiCopy className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 transition-colors ${
                                copied
                                    ? 'text-success-600 dark:text-success-400'
                                    : 'text-gray-500 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                            }`} />
                            <div className="text-left flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {copied ? 'Copied!' : 'Copy Story Link'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {storyUrl}
                                </p>
                            </div>
                            {copied && (
                                <span className="text-sm font-semibold text-success-600 dark:text-success-400 whitespace-nowrap">
                                    ✓
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Social Share Section */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">
                            Share on Social
                        </h3>
                        <div className="overflow-x-auto -mx-1 px-1 scrollbar-hide">
                            <div className="flex gap-2 sm:gap-3 min-w-min">
                                {shareOptions.map((option) => {
                                    const IconComponent = option.icon
                                    return (
                                        <button
                                            key={option.id}
                                            onClick={() => handleSocialShare(option.url, option.name)}
                                            className={`flex flex-col items-center justify-center py-3 px-3 sm:px-4 rounded-lg border border-gray-200 dark:border-gray-700 transition-all flex-shrink-0 min-w-max ${option.color}`}
                                            title={option.name}
                                            aria-label={`Share on ${option.name}`}
                                        >
                                            <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 mb-1 sm:mb-2" />
                                            <span className="text-xs sm:text-sm font-medium text-center whitespace-nowrap">
                                                {option.name}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Share Tips */}
                    <div className="mt-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            💡 <strong>Pro Tip:</strong> Share your story to inspire others and grow your professional network!
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors text-base"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ShareModal